import { viewEmailText } from 'owa-locstrings/lib/strings/viewemailtext.locstring.json';
import {
    viewCalendarText,
    addToCalendarText,
    removeFromCalendarLoaderText,
} from '../strings.locstring.json';
import loc from 'owa-localize';
import { observer } from 'mobx-react';
import ActionButton from './ActionButton';
import TailoredActionName from '../store/schema/telemetry/TailoredActionName';
import getEditableCustomData from '../utils/telemetry/getEditableCustomData';
import ButtonColors from '../store/viewSchema/ButtonColors';
import logActionDatapoint from '../utils/telemetry/logActionDatapoint';
import getColor from '../utils/view/getColor';
import { IconButton } from 'office-ui-fabric-react/lib/Button';
import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import * as React from 'react';
import TxpConfiguration from 'txp-data/lib/schema/configSchema/TxpConfiguration';
import ActionButtonData from 'txp-data/lib/schema/viewSchema/ActionButtonData';
import ActionButtonStyle from 'txp-data/lib/schema/viewSchema/ActionButtonStyle';
import CardItem from 'txp-data/lib/schema/viewSchema/CardItem';
import TxpStyle from 'txp-data/lib/schema/viewSchema/TxpStyle';

let styles = require('./Txp.scss');
let classNames = require('classnames');

export interface ActionButtonsProps {
    cardItem: CardItem;
    txpConfiguration: TxpConfiguration;
    txpStyle: TxpStyle;
    isSpinnerShown?: boolean;
    onSpinnerStateChanged?: (state: boolean) => void;
    onActionButtonClicked?: () => void;
}

@observer
export default class ActionButtons extends React.Component<ActionButtonsProps> {
    UNSAFE_componentWillReceiveProps(newProps: ActionButtonsProps) {
        if (newProps.cardItem.calendarEventId != this.props.cardItem.calendarEventId) {
            this.props.onSpinnerStateChanged(false);
        }
    }

    render() {
        const { cardItem, txpConfiguration, onSpinnerStateChanged } = this.props;
        const { calendarEventId } = cardItem;
        const {
            onAddToCalendarClicked,
            onViewInMailClicked,
            txpImages,
            getActionButtonProperties,
            getContextMenuItems,
        } = txpConfiguration;
        let { isSpinnerShown, txpStyle } = this.props;
        let hasDetailsIcons: boolean = !!(txpImages && txpImages.detailsIcons);

        let actionButtonDataProperties = cardItem.actionButtonData;

        if (getActionButtonProperties) {
            const configActionButtonData = getActionButtonProperties(txpConfiguration, cardItem);

            if (configActionButtonData) {
                actionButtonDataProperties = configActionButtonData;
            }
        }

        let showActionButton = !!actionButtonDataProperties;
        let showViewInMailButton = onViewInMailClicked;

        //Contains context menu items to be shown in Action button Drop down.
        let txpActionItems: IContextualMenuItem[] = getContextMenuItems
            ? getContextMenuItems(txpConfiguration, cardItem, onSpinnerStateChanged)
            : null;

        let showContextMenu = txpActionItems && txpActionItems.length > 0;

        let shouldShowActionButtons =
            showActionButton || calendarEventId || showViewInMailButton || txpActionItems;

        // Show either Add to Calendar or View in Calendar based on calendarEventId
        let calendarActionElement: JSX.Element = null;
        if (calendarEventId) {
            if (isSpinnerShown) {
                calendarActionElement = this.removeFromCalendarButton();
            } else {
                calendarActionElement = this.viewInCalendarButton(hasDetailsIcons);
            }
        } else {
            if (onAddToCalendarClicked) {
                calendarActionElement = this.addToCalendarButton(hasDetailsIcons);
            }
        }

        if (shouldShowActionButtons) {
            return (
                <div
                    style={
                        txpStyle.lighterAltColor ? { background: txpStyle.lighterAltColor } : null
                    }
                    className={this.props.txpStyle.actionButtonsContainer}>
                    {calendarActionElement}
                    {showViewInMailButton && this.viewInMailButton(hasDetailsIcons)}
                    {showActionButton && this.actionButton(actionButtonDataProperties)}
                    {showContextMenu && (
                        <IconButton
                            className={styles.txpCommandBar}
                            menuProps={{
                                items: txpActionItems,
                            }}
                        />
                    )}
                </div>
            );
        } else {
            return null;
        }
    }

    private viewInCalendarButton = (hasDetailsIcons: boolean): JSX.Element => {
        const { txpConfiguration, cardItem } = this.props;
        const editableCustomData = getEditableCustomData(txpConfiguration, cardItem);

        let onViewInCalendar = () =>
            logActionDatapoint(
                TailoredActionName.ViewCalendar,
                txpConfiguration.datapointLogger,
                cardItem.activityType,
                editableCustomData
            );

        let viewInCalendarStyle: ActionButtonStyle = {
            buttonStyle: styles.viewButton,
            buttonTextStyle: styles.actionButtonText,
            iconStyle: styles.buttonImage,
        };

        let { getCalendarDeepLinkUrl, txpImages } = txpConfiguration;
        let calendarIcon = hasDetailsIcons ? txpImages.detailsIcons.calendar : null;
        return (
            <ActionButton
                onClickHandler={onViewInCalendar}
                style={viewInCalendarStyle}
                anchorLink={getCalendarDeepLinkUrl(this.props.cardItem.calendarEventId)}
                controlIconName={calendarIcon}
                target="_blank"
                buttonElementText={loc(viewCalendarText)}
            />
        );
    };

    private viewInMailButton = (hasDetailsIcons: boolean): JSX.Element => {
        let onViewInMail = () => {
            logActionDatapoint(
                TailoredActionName.ViewEmail,
                this.props.txpConfiguration.datapointLogger,
                this.props.cardItem.activityType
            );
            this.props.txpConfiguration.onViewInMailClicked();
        };
        let { txpImages } = this.props.txpConfiguration;
        let mailIcon = hasDetailsIcons ? txpImages.detailsIcons.mail : null;
        let viewInMailButtonStyle: ActionButtonStyle = this.getActionButtonStyle(styles.viewButton);

        return (
            <ActionButton
                buttonElementText={loc(viewEmailText)}
                style={viewInMailButtonStyle}
                controlIconName={mailIcon}
                onClickHandler={onViewInMail}
                showSpinner={false}
            />
        );
    };

    /* JSX Element to show the action button */
    private actionButton = (actionButtonData: ActionButtonData): JSX.Element => {
        const { actionIconName, actionButtonText, actionUrl, actionName } = actionButtonData;
        const { txpConfiguration, cardItem } = this.props;
        const onActionButton = () => {
            cardItem &&
                logActionDatapoint(
                    actionName,
                    txpConfiguration.datapointLogger,
                    cardItem.activityType
                );

            if (actionUrl) {
                window.open(actionUrl);
            } else if (this.props.onActionButtonClicked) {
                this.props.onActionButtonClicked();
            }
        };

        let actionButtonStyle: ActionButtonStyle = this.getActionButtonStyle(styles.actionButton);

        return (
            <ActionButton
                buttonElementText={actionButtonText}
                style={actionButtonStyle}
                controlIconName={actionIconName}
                onClickHandler={onActionButton}
                showSpinner={false}
            />
        );
    };

    /* JSX Element to show "Add to calendar" */
    private addToCalendarButton = (hasDetailsIcons: boolean): JSX.Element => {
        let { isSpinnerShown, onSpinnerStateChanged, txpConfiguration, cardItem } = this.props;
        let { onAddToCalendarClicked, txpImages } = txpConfiguration;
        const editableCustomData = getEditableCustomData(txpConfiguration, cardItem);

        let onAddToCalendarClickedHandler = () => {
            onSpinnerStateChanged(true);
            logActionDatapoint(
                TailoredActionName.AddToCalendar,
                txpConfiguration.datapointLogger,
                cardItem.activityType,
                editableCustomData
            );
            onAddToCalendarClicked();
        };

        let addToCalendarStyle: ActionButtonStyle = {
            buttonStyle: isSpinnerShown
                ? classNames(styles.calendarActionButtonDefault, styles.calendarActionButton)
                : styles.calendarActionButton,
            buttonTextStyle: styles.actionButtonText,
            iconStyle: styles.calendarActionButtonImage,
        };

        if (isSpinnerShown) {
            return (
                <ActionButton
                    buttonElementText={loc(addToCalendarText)}
                    style={addToCalendarStyle}
                    showSpinner={true}
                />
            );
        } else {
            return (
                <ActionButton
                    buttonElementText={loc(addToCalendarText)}
                    style={addToCalendarStyle}
                    controlIconName={hasDetailsIcons ? txpImages.detailsIcons.calendar : ''}
                    onClickHandler={onAddToCalendarClickedHandler}
                    showSpinner={false}
                />
            );
        }
    };

    /**
     * JSX Element to show removing from Calendar with spinner
     */
    private removeFromCalendarButton = (): JSX.Element => {
        let removingElementStyle: ActionButtonStyle = {
            buttonStyle: classNames(
                styles.calendarActionButtonDefault,
                styles.calendarActionButton
            ),
            buttonTextStyle: styles.actionButtonText,
            iconStyle: styles.calendarActionButtonImage,
        };

        return (
            <ActionButton
                buttonElementText={loc(removeFromCalendarLoaderText)}
                style={removingElementStyle}
                showSpinner={true}
            />
        );
    };

    private getActionButtonStyle = (style: string): ActionButtonStyle => {
        const { txpStyle } = this.props;

        let actionButtonStyle: ActionButtonStyle = {
            buttonTextStyle: styles.actionButtonText,
            iconStyle: styles.buttonImage,
            themeColor: getColor(ButtonColors.Primary, txpStyle),
            darkerColor: getColor(ButtonColors.Darker, txpStyle),
            lightColor: getColor(ButtonColors.Light, txpStyle),
            lighterColor: getColor(ButtonColors.Lighter, txpStyle),
            darkColor: getColor(ButtonColors.Dark, txpStyle),
            buttonStyle: style,
        };
        return actionButtonStyle;
    };
}
